import { useEffect, useRef, useState } from "react";
import apiUtils, { isApiError } from "../apiUtils";
import logger from "../utils/logger";
import type { EnrichedAppointment } from "../utils/schedule";

/** The JIT pre-warm horizon in milliseconds (5 minutes before appointment start). */
export const PREWARM_WINDOW_MS = 5 * 60 * 1000;

export interface UseSchedulePreloadOptions {
  enriched: EnrichedAppointment[];
  now: Date;
  enabled?: boolean;
}

export interface UseSchedulePreloadResult {
  prewarmedPatientIds: string[];
  activePrewarmingId: string | null;
}

/**
 * Schedule-driven Just-In-Time (JIT) pre-warming hook.
 *
 * Implements clinical schedule pre-warming: outpatient clinicians review patient charts
 * ~5 minutes prior to encounters. Identifies upcoming appointments within T-5 min
 * (0 <= T_start - now <= 5 min) or currently active appointments, and triggers:
 * 1. POST /api/documents/populate-sources with { source: "schedule_prewarm" }
 *    (gates on provider feature activity; skips dormant providers without retry loops)
 * 2. GET /api/documents?patient_id={patientId}&type=source (primes documents list metadata when active)
 *
 * Bounds concurrency to 1 patient per provider and prevents redundant calls using a Set ref.
 */
export function useSchedulePreload({
  enriched,
  now,
  enabled = true,
}: UseSchedulePreloadOptions): UseSchedulePreloadResult {
  const prewarmedRef = useRef<Set<string>>(new Set());
  const failedRef = useRef<Set<string>>(new Set());
  const inFlightRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const [prewarmedList, setPrewarmedList] = useState<string[]>([]);
  const [activePrewarmingId, setActivePrewarmingId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (inFlightRef.current !== null) return;
    if (!enabled || !enriched || enriched.length === 0) {
      return;
    }

    const nowMs = now.getTime();

    // Find the next upcoming appointment within the 5-minute pre-warm window
    // (or currently active encounter) that hasn't already been pre-warmed or in flight.
    const candidate = enriched.find((appt) => {
      if (!appt.patientId || !appt.start) return false;
      if (prewarmedRef.current.has(appt.patientId)) return false;
      if (failedRef.current.has(appt.patientId)) return false;

      const startMs = appt.start.getTime();
      const endMs = appt.end ? appt.end.getTime() : startMs + 15 * 60 * 1000;
      const diffMs = startMs - nowMs;

      // Within 5 min before start (0 <= diff <= 5m) or currently ongoing (start <= now < end)
      const isDue =
        (diffMs >= 0 && diffMs <= PREWARM_WINDOW_MS) ||
        (startMs <= nowMs && nowMs < endMs);
      return isDue;
    });

    if (!candidate || !candidate.patientId) {
      return;
    }

    const patientId = candidate.patientId;
    inFlightRef.current = patientId;
    setActivePrewarmingId(patientId);

    async function prewarm() {
      try {
        logger.devOnly.debug(
          "useSchedulePreload: JIT pre-warming sources for patient",
          { patient_id: patientId },
        );

        // 1. Populate sources from warehouse
        const popResp = (await apiUtils.fetchData(
          "/api/documents/populate-sources",
          "POST",
          { patient_id: patientId, source: "schedule_prewarm" },
        )) as Record<string, unknown> | null;
        if (!isMountedRef.current) return;
        if (isApiError(popResp)) {
          failedRef.current.add(patientId);
          logger.devOnly.warn(
            "useSchedulePreload: populate-sources failed during pre-warming",
            {
              patient_id: patientId,
              error: popResp.error,
            },
          );
          return;
        }

        // If skipped (e.g. dormant provider), mark prewarmed so we don't retry and skip document prefetch
        if (popResp && popResp.status === "skipped") {
          logger.devOnly.debug(
            "useSchedulePreload: pre-warming skipped (dormant provider)",
            { patient_id: patientId, reason: popResp.reason },
          );
          if (isMountedRef.current) {
            prewarmedRef.current.add(patientId);
            setPrewarmedList(Array.from(prewarmedRef.current));
          }
          return;
        }

        // 2. Prefetch source documents list
        await apiUtils.fetchData(
          `/api/documents?patient_id=${encodeURIComponent(patientId)}&type=source`,
          "GET",
        );

        if (isMountedRef.current) {
          prewarmedRef.current.add(patientId);
          setPrewarmedList(Array.from(prewarmedRef.current));
        }
      } catch (err) {
        failedRef.current.add(patientId);
        logger.devOnly.warn("useSchedulePreload: error during JIT pre-warm", {
          patient_id: patientId,
          error: err,
        });
      } finally {
        if (inFlightRef.current === patientId) {
          inFlightRef.current = null;
          if (isMountedRef.current) {
            setActivePrewarmingId(null);
          }
        }
      }
    }

    prewarm();
  }, [enriched, now, enabled]);

  return {
    prewarmedPatientIds: prewarmedList,
    activePrewarmingId,
  };
}

export default useSchedulePreload;
